import type {
  AisleCabinet,
  MachineId,
  MetaVerb,
  SetzenPayload,
  SimInput,
  RunPhase,
  RunState,
  SimEvent,
  Snapshot,
  Tag,
  TokenSpec,
  Verb,
} from '../shared/contract.js';
import {
  DT,
  TICKET_RATIO,
  TICK_HZ,
  TOKEN_BASE_FRICTION,
  TOKEN_BASE_MASS,
  TOKEN_BASE_RESTITUTION,
  TOKEN_HALF_HEIGHT,
} from './const.js';
import { Economy, floorMultiplier } from './economy.js';
import { Hasher } from './hash.js';
import { itemRefs, modifiersFor } from './items/catalogue.js';
import { Bandit, type BanditSetzen } from './machines/bandit.js';
import { bends, Cabinet, type CabinetHost } from './machines/base.js';
import { Flipper } from './machines/flipper.js';
import { Greifer } from './machines/greifer.js';
import { Muenzschieber } from './machines/muenzschieber.js';
import { Pachinko } from './machines/pachinko.js';
import { Roulette } from './machines/roulette.js';
import {
  ctxFor,
  type EscapeMode,
  type Modifier,
  Pipeline,
  type ScoreInfo,
  type SimApi,
} from './modifier.js';
import { initPhysics, PhysicsWorld } from './physics/world.js';
import { orderKey, runPayout } from './payout.js';
import { RngStreams } from './rng.js';
import { tagDefaults } from './tags.js';
import { makeRuntime, MATERIAL_MULT, type TokenRuntime } from './token.js';
import { dist, v3, type Vec3 } from './vec.js';

/**
 * THE SIM. A pure function of (seed, input log).
 *
 * No Date.now, no Math.random, no DOM, no renderer — the covenant lint enforces all
 * four. Everything the renderer will ever animate leaves through `SimEvent`s, so this
 * file is deliberately generous about emitting: near-misses, every payout stage, splits,
 * phases, zinsTicks, the lot.
 */

const MATERIAL_INDEX: Record<string, number> = { kupfer: 0, silber: 1, gold: 2, obsidian: 3 };
const TAG_INDEX: Record<Tag, number> = {
  Magnet: 0, Geist: 1, Gummi: 2, Kleber: 3, Spalt: 4, Gold: 5,
  Fluch: 6, Feuer: 7, Gluecks: 8, Schwer: 9, Zins: 10, Echo: 11,
};

/**
 * The arcade floor is one contiguous room; cabinets are neighbours in it. These are the
 * coordinates `TokenState.pos` is expressed in (contract v1) and the same numbers the
 * renderer subtracts via `CabinetState.origin`.
 */
export const CABINET_ORIGIN: Record<MachineId, Vec3> = {
  pachinko: v3(0, 0, 0),
  muenzschieber: v3(16, 0, 0),
  bandit: v3(32, 0, 0),
  roulette: v3(48, 0, 0),
  greifer: v3(64, 0, 0),
  /**
   * The Saalchef's table stands apart from the aisle: it is behind the door, and the
   * door only opens on the ticket quota. Same shared world, same coordinates, so Der
   * Deckel could in principle carry a ball out of the boss table — which is exactly the
   * sort of thing the one-world decision was made for.
   */
  flipper: v3(84, 0, 0),
};
/** Who catches what falls out of whom (Der Deckel). Adjacency in the aisle, nothing more. */
const NEIGHBOUR: Partial<Record<MachineId, MachineId>> = {
  pachinko: 'muenzschieber',
  muenzschieber: 'pachinko',
  bandit: 'muenzschieber',
  roulette: 'bandit',
  greifer: 'roulette',
};

/** Engine guard: a runaway [Spalt] cascade ends at the cabinet limit, but memory is
 *  finite, so the sim refuses to split past this. Documented cut, not a balance nerf. */
const MAX_LIVE_TOKENS = 900;

export interface SimOptions {
  seed: number;
  /** Der Beutel — the player's deck */
  beutel?: TokenSpec[];
  /** charm / manipulation / ZINKEN ids from the catalogue */
  items?: string[];
  /** extra modifiers (tests, House riggings built on the fly) */
  modifiers?: Modifier[];
  startingCoins?: number;
  floor?: number;
  machines?: MachineId[];
  /** seed the 300-token MÜNZSCHIEBER pile */
  seedPile?: boolean;
}

/**
 * contract v1 owns these shapes now: the shell builds the input log, and replay
 * determinism depends on it building them exactly right, so both sides read one
 * definition. Re-exported here so core's own consumers need not know where they moved.
 */
export type { SetzenPayload, SimInput } from '../shared/contract.js';

/** One token as a save file sees it: its spec plus the runtime that makes a pile a pile. */
export interface TokenSnapshot {
  spec: TokenSpec;
  pos: [number, number, number];
  quat: [number, number, number, number];
  vel: [number, number, number];
  age: number;
  zinsClock: number;
  potency: number;
  stuck: boolean;
  generation: number;
}

export interface CabinetSnapshot {
  machine: MachineId;
  topf: number;
  straehne: number;
  limit01: number;
  broken: boolean;
  stakeLevel: number;
  entries: number;
  tokens: TokenSnapshot[];
}

interface EchoEntry {
  token: number;
  value: number;
  dueTick: number;
  pocket: string;
  machine: MachineId;
  material: TokenRuntime['material'];
  tags: Tag[];
  at: Vec3;
}

export class Sim implements CabinetHost, SimApi {
  readonly seed: number;
  readonly rng: RngStreams;
  readonly pipe = new Pipeline();
  readonly phys: PhysicsWorld;
  readonly economy: Economy;
  readonly dt = DT;

  tick = 0;
  activeMachine: MachineId | null = null;

  /**
   * Run-shell state. The RunDirector (core/run.ts) owns the *decisions*; the Sim owns the
   * numbers so that `snapshot()` stays the single door to the renderer and everything
   * that varies is inside the world hash.
   */
  phase: RunPhase = 'aisle';
  ticketQuota = 0;
  /** this floor's wonder machine, and the FOTOAUTOMAT photograph you are holding */
  wunder: string | null = null;
  /** what this floor's wonder machine is offering (contract v2); the director fills it */
  wunderCard: { id: string; name: string; text: string; price: number }[] = [];
  fotoHeld: string | null = null;
  over = false;
  won = false;
  /** the little figure in the aisle; walking is an input, so it is part of the replay */
  spielerX = 0;
  spielerZ = 8;

  private readonly cabinets = new Map<MachineId, Cabinet<never>>();
  private readonly tokens = new Map<number, TokenRuntime>();
  private readonly order: number[] = [];
  private readonly tokenByBody = new Map<number, number>();
  private nextTokenId = 1;

  private events: SimEvent[] = [];
  private echoes: EchoEntry[] = [];
  /** rolling five seconds of winnings, for the "winning loudly" heat meter */
  private winWindow: { tick: number; amount: number }[] = [];
  private beutel: TokenSpec[];
  private itemIds: string[];

  private posCache = new Map<number, Vec3>();
  private posVersion = 0;
  private posCacheVersion = -1;
  private tokensByMachine = new Map<MachineId, TokenRuntime[]>();
  private tokensCacheTick = -1;
  private tokensDirty = true;

  /** Sim must be built through create(): Rapier's WASM needs an await. */
  static async create(opts: SimOptions): Promise<Sim> {
    await initPhysics();
    return new Sim(opts);
  }

  constructor(opts: SimOptions) {
    this.seed = opts.seed >>> 0;
    this.rng = new RngStreams(this.seed);
    this.phys = new PhysicsWorld(v3(0, -9.81, 0), DT);
    this.economy = new Economy(opts.startingCoins ?? 100);
    this.economy.floor = opts.floor ?? 1;
    this.beutel = (opts.beutel ?? defaultBeutel()).map((s) => ({ ...s, tags: [...s.tags] }));
    this.itemIds = [...(opts.items ?? [])];

    // the grammar: tag defaults first, then items, then anything bespoke
    this.pipe.addAll(tagDefaults());
    this.pipe.addAll(modifiersFor(this.itemIds));
    if (opts.modifiers) this.pipe.addAll(opts.modifiers);

    const wanted = opts.machines ?? ['pachinko', 'muenzschieber', 'bandit'];
    for (const id of wanted) this.buildCabinet(id);
    if (opts.seedPile) this.pile()?.seedPile();
    /**
     * **You start in the aisle, at nothing.** v0 parked `activeMachine` on the first
     * cabinet so headless tests could drive a machine without a director — and that made
     * the *booted game* claim you were standing at a machine you had never entered and
     * never paid for. Two real defects fell out of it, both found by playing rather than
     * by reading:
     *
     *   1. the shell's E key toggles enter/leave off this field, so the very first E a
     *      player pressed *left* a cabinet they were not in, and every verb after that
     *      was a no-op until they pressed E a second time;
     *   2. Setzen from the aisle dropped a token into the pachinko field for free — no
     *      buy-in charged, no cabinet entered. An unattended cabinet resolving landings
     *      is the money printer STATUS.md already warns about, in a new costume.
     *
     * Tests that want a cabinet active assign this field directly (they all already do).
     * The game enters a cabinet exactly one way: the `enter` meta verb, which charges.
     */
    this.activeMachine = null;
  }

  private buildCabinet(id: MachineId): void {
    const origin = CABINET_ORIGIN[id] ?? v3(0, 0, 0);
    let c: Cabinet<never> | null = null;
    if (id === 'pachinko') c = new Pachinko(this, origin) as unknown as Cabinet<never>;
    else if (id === 'muenzschieber') c = new Muenzschieber(this, origin) as unknown as Cabinet<never>;
    else if (id === 'bandit') c = new Bandit(this, origin) as unknown as Cabinet<never>;
    else if (id === 'roulette') c = new Roulette(this, origin) as unknown as Cabinet<never>;
    else if (id === 'greifer') c = new Greifer(this, origin) as unknown as Cabinet<never>;
    else if (id === 'flipper') c = new Flipper(this, origin) as unknown as Cabinet<never>;
    if (!c) return;
    c.build();
    this.cabinets.set(id, c);
  }

  cabinet(id: MachineId): Cabinet<never> | null {
    return this.cabinets.get(id) ?? null;
  }

  pachinko(): Pachinko | null {
    return (this.cabinets.get('pachinko') as unknown as Pachinko) ?? null;
  }

  pile(): Muenzschieber | null {
    return (this.cabinets.get('muenzschieber') as unknown as Muenzschieber) ?? null;
  }

  bandit(): Bandit | null {
    return (this.cabinets.get('bandit') as unknown as Bandit) ?? null;
  }

  roulette(): Roulette | null {
    return (this.cabinets.get('roulette') as unknown as Roulette) ?? null;
  }

  /** Der Greifer IS the shop; the run director stocks it and drains what you win. */
  greifer(): Greifer | null {
    return (this.cabinets.get('greifer') as unknown as Greifer) ?? null;
  }

  /** The Saalchef's table. Built like any cabinet; only reachable through the door. */
  flipper(): Flipper | null {
    return (this.cabinets.get('flipper') as unknown as Flipper) ?? null;
  }

  // ------------------------------------------------------------------ SimApi

  get machine(): MachineId {
    return this.activeMachine ?? 'pachinko';
  }

  get api(): SimApi {
    return this;
  }

  /**
   * Events go two places: the per-frame array the renderer drains through `snapshot()`,
   * and — if anyone is listening — straight to an observer, at emit time.
   *
   * The observer exists because the two consumers have genuinely different needs. The
   * renderer wants "everything since the last frame" and drains it. The run director wants
   * "everything, exactly once, ever" and must not care when (or whether) somebody else
   * drained. Reading `peekEvents()` on a timer looks like it does the second job and does
   * not: whatever has not been drained yet is re-read on the next call, so a chronicle
   * built that way counts one payout hundreds of times and prints a receipt claiming
   * fifty-eight million coins from sixty-eight stakes. Ask me how I know.
   */
  emit(e: SimEvent): void {
    this.events.push(e);
    if (this.observer) this.observer(e);
  }

  /** Called once per event, at emit time. Used by the run director's chronicle. */
  observer: ((e: SimEvent) => void) | null = null;

  token(id: number): TokenRuntime | null {
    const t = this.tokens.get(id);
    return t && t.alive ? t : null;
  }

  liveTokens(): readonly TokenRuntime[] {
    const out: TokenRuntime[] = [];
    for (const id of this.order) {
      const t = this.tokens.get(id);
      if (t && t.alive && t.machine === this.machine) out.push(t);
    }
    return out;
  }

  /**
   * Per-tick cached. Called several times per tick per cabinet (motion, scoring,
   * structural load, snapshots); rebuilding a 300-entry array each time was pure waste.
   */
  tokensIn(machine: MachineId): TokenRuntime[] {
    if (this.tokensDirty || this.tokensCacheTick !== this.tick) {
      this.tokensByMachine.clear();
      for (const id of this.order) {
        const t = this.tokens.get(id);
        if (!t || !t.alive) continue;
        let arr = this.tokensByMachine.get(t.machine);
        if (!arr) {
          arr = [];
          this.tokensByMachine.set(t.machine, arr);
        }
        arr.push(t);
      }
      this.tokensCacheTick = this.tick;
      this.tokensDirty = false;
    }
    return this.tokensByMachine.get(machine) ?? EMPTY_TOKENS;
  }

  position(id: number): Vec3 | null {
    const t = this.tokens.get(id);
    if (!t || !t.alive || t.body < 0) return null;
    if (this.posCacheVersion !== this.posVersion) {
      this.posCache.clear();
      this.posCacheVersion = this.posVersion;
    }
    let p = this.posCache.get(id);
    if (!p) {
      p = this.phys.translation(t.body);
      this.posCache.set(id, p);
    }
    return p;
  }

  /**
   * Neighbours within `radius`, NEAREST FIRST (ties broken by id, so the order is
   * total and deterministic).
   *
   * Sorting matters: callers cap the work they do per tick, and in a 300-coin pile a
   * radius query returns ~150 hits. Returning them in id order meant a [Magnet] token
   * capped at 12 was pulling on the twelve oldest coins in the pile rather than the
   * twelve closest — a magnet that measurably did nothing.
   */
  neighbours(id: number, radius: number): number[] {
    const self = this.tokens.get(id);
    const here = this.position(id);
    if (!self || !here) return [];
    const hits: { id: number; d: number }[] = [];
    for (const oid of this.order) {
      if (oid === id) continue;
      const t = this.tokens.get(oid);
      if (!t || !t.alive || t.machine !== self.machine || t.body < 0) continue;
      const p = this.position(oid);
      if (!p) continue;
      const d = dist(here, p);
      if (d <= radius) hits.push({ id: oid, d });
    }
    hits.sort((a, b) => a.d - b.d || a.id - b.id);
    return hits.map((h) => h.id);
  }

  /** Exact overlap test against core-owned geometry — engine-free, so bit-stable. */
  solidOverlap(id: number): number | null {
    const t = this.tokens.get(id);
    const here = this.position(id);
    if (!t || !here) return null;

    const cab = this.cabinets.get(t.machine);
    if (cab) {
      for (const s of cab.fieldSolids()) {
        if (s.kind === 'ball') {
          if (dist(here, s.pos) <= s.r + t.radius) return -1;
        } else {
          const dx = Math.max(0, Math.abs(here.x - s.pos.x) - s.h.x);
          const dy = Math.max(0, Math.abs(here.y - s.pos.y) - s.h.y);
          const dz = Math.max(0, Math.abs(here.z - s.pos.z) - s.h.z);
          if (Math.sqrt(dx * dx + dy * dy + dz * dz) <= t.radius) return -1;
        }
      }
    }
    for (const oid of this.order) {
      if (oid === id) continue;
      const o = this.tokens.get(oid);
      if (!o || !o.alive || o.machine !== t.machine || o.body < 0) continue;
      const p = this.position(oid);
      if (p && dist(here, p) <= t.radius + o.radius) return oid;
    }
    return null;
  }

  impulse(id: number, v: Vec3): void {
    const t = this.tokens.get(id);
    if (!t || !t.alive || t.body < 0 || t.stuck) return;
    // anything that pushes a sedimented coin wakes it back up — this is what lets a
    // [Magnet] token dragged into the pile interior actually move the pile
    if (t.sedimented) {
      t.sedimented = false;
      this.phys.setSedimented(t.body, false);
      this.posVersion++;
    }
    this.phys.impulse(t.body, v);
  }

  freeze(id: number): void {
    const t = this.tokens.get(id);
    if (!t || !t.alive || t.body < 0 || t.stuck) return;
    t.stuck = true;
    this.phys.freeze(t.body);
  }

  setGhost(id: number, on: boolean): void {
    const t = this.tokens.get(id);
    if (t && t.alive && t.body >= 0) this.phys.setGhost(t.body, on);
  }

  setFaceValue(id: number, v: number): void {
    const t = this.tokens.get(id);
    if (t) t.faceValue = v;
  }

  splitToken(id: number): [number, number] | null {
    const t = this.tokens.get(id);
    if (!t || !t.alive) return null;
    if (this.order.length >= MAX_LIVE_TOKENS) return null;
    const at = this.position(id) ?? t.lastScore?.at ?? v3(0, 0, 0);
    const half = t.faceValue / 2;
    const kids: number[] = [];
    for (const s of [-1, 1]) {
      const spec: TokenSpec = {
        id: -1,
        material: t.material,
        faceValue: half,
        radius: Math.max(0.28, t.radius * 0.8),
        tags: [...t.tags],
      };
      const child = this.spawnToken(
        spec,
        t.machine,
        { x: at.x + s * (t.radius + 0.1), y: at.y + 0.35, z: at.z },
        { x: s * 1.6, y: 2.0, z: 0 },
      );
      // "inherit enchants at half potency"; splits reset the ripening clock, so a
      // [Spalt]+[Zins] player has to decide when to cascade. Timing is the skill.
      child.potency = t.potency / 2;
      child.zinsClock = 0;
      child.generation = t.generation + 1;
      kids.push(child.id);
    }
    this.emit({ t: 'split', parent: id, children: [kids[0]!, kids[1]!], at: [at.x, at.y, at.z] });
    return [kids[0]!, kids[1]!];
  }

  scheduleEcho(id: number, value: number, delaySec: number): void {
    const t = this.tokens.get(id);
    if (!t || !t.lastScore) return;
    this.echoes.push({
      token: id,
      value,
      dueTick: this.tick + Math.round(delaySec / DT),
      pocket: 'echo:' + t.lastScore.pocket,
      machine: t.machine,
      material: t.material,
      tags: [...t.tags],
      at: t.lastScore.at,
    });
  }

  addTopf(amount: number): void {
    const c = this.cabinets.get(this.machine);
    if (c) c.topf += amount;
  }

  score(info: ScoreInfo): void {
    const c = this.cabinets.get(this.machine);
    if (c) this.award(info, c);
  }

  verdacht(delta: number, reason: string): void {
    this.economy.addVerdacht(delta, reason, (e) => this.emit(e));
  }

  glueck(delta: number): void {
    if (delta > 0) {
      this.economy.glueck = Math.min(10, this.economy.glueck + delta);
      this.emit({ t: 'glueckRegen', amount: delta });
    } else {
      this.economy.spendGlueck(-delta);
    }
  }

  // ------------------------------------------------------------------ CabinetHost

  spawnToken(spec: TokenSpec, machine: MachineId, pos: Vec3, vel: Vec3, physical = true): TokenRuntime {
    const id = this.nextTokenId++;
    const t = makeRuntime({ ...spec, id, tags: [...spec.tags] }, machine);
    t.live = true;
    this.tokens.set(id, t);
    this.order.push(id);

    if (physical) {
      const ctx = ctxFor(machine, this.tick, t);
      const mass = this.pipe.query('mass', ctx, TOKEN_BASE_MASS);
      const restitution = this.pipe.query('restitution', ctx, TOKEN_BASE_RESTITUTION);
      const friction = this.pipe.query('friction', ctx, TOKEN_BASE_FRICTION);
      t.mass = mass;
      t.body = this.phys.addToken(pos, vel, {
        mass,
        restitution,
        friction,
        radius: t.radius,
        halfHeight: TOKEN_HALF_HEIGHT,
      });
      this.tokenByBody.set(t.body, id);
      this.posVersion++;
    }
    this.tokensDirty = true;
    return t;
  }

  removeToken(id: number, _reason: 'scored' | 'lost' | 'transfer'): void {
    const t = this.tokens.get(id);
    if (!t || !t.alive) return;
    t.alive = false;
    t.live = false;
    if (t.body >= 0) {
      this.tokenByBody.delete(t.body);
      this.phys.removeBody(t.body);
      t.body = -1;
      this.posVersion++;
    }
    const i = this.order.indexOf(id);
    if (i >= 0) this.order.splice(i, 1);
    this.tokens.delete(id);
    this.tokensDirty = true;
  }

  /** Runs the whole payout pipeline for one scoring event. */
  award(info: ScoreInfo, cabinet: Cabinet<never>): { muenzen: number; tickets: number } {
    const t = this.tokens.get(info.token) ?? null;
    const res = runPayout(
      this.pipe,
      {
        token: t,
        machine: cabinet.id,
        tick: this.tick,
        base: info.value,
        jackpot: info.jackpot,
        straehne: cabinet.straehne,
        topf: cabinet.topf,
        floorMult: floorMultiplier(this.economy.floor),
        ticketRatio: TICKET_RATIO[cabinet.id] ?? 0.25,
      },
      (e) => this.emit(e),
    );

    this.economy.addCoins(res.muenzen);
    this.economy.addTickets(res.tickets);
    this.emit({ t: 'payout', muenzen: res.muenzen, tickets: res.tickets, machine: cabinet.id });

    const evCtx = { ...ctxFor(cabinet.id, this.tick, t), api: this as SimApi };
    for (const s of res.stages) {
      this.pipe.fire('onPayoutStage', evCtx, { stage: s.stage, before: s.before, after: s.after });
    }
    // hooks that react to scoring ([Spalt] splits, [Echo] schedules) run after the maths
    this.pipe.fire('onScore', evCtx, info);

    // a zero payout is a loss, and losses pay the pity engine — this is the line that
    // makes a [Fluch] tank build actually farm GLÜCK
    if (res.total <= 0) this.economy.registerLoss((e) => this.emit(e));
    // winning loudly is what the House minds, not winning
    if (info.jackpot) this.economy.addVerdacht(3, 'Jackpot', (e) => this.emit(e));
    // …and this is the rest of house-and-heat.md's first clause: VERDACHT rises on
    // "winning streaks above expectation". Banked here, converted once a second in
    // `step()` — see `chargeLoudness`.
    this.winWindow.push({ tick: this.tick, amount: res.muenzen });

    return { muenzen: res.muenzen, tickets: res.tickets };
  }

  /** One labelled, GLÜCK-bendable, [Fluch]-invertible roll. */
  roll(stream: string, event: string, p: number, token: TokenRuntime | null): boolean {
    const ctx = { ...ctxFor(this.machine, this.tick, token), stream, event };
    let pp = this.pipe.query('odds', ctx, p);
    pp = pp < 0 ? 0 : pp > 1 ? 1 : pp;

    let win = this.rng.float(stream) < pp;
    const key = `${stream}|${event}`;
    const pending = bends(this);
    if (pending.has(key)) {
      pending.delete(key);
      // GLÜCK buys exactly one reroll of one declared event — visible, honest, bounded
      if (!win) win = this.rng.float(stream) < pp;
    }
    this.pipe.fire(
      'onRoll',
      { ...ctxFor(this.machine, this.tick, token), api: this as SimApi },
      { stream, event, win },
    );
    return win;
  }

  drawFromBeutel(stream: string): TokenSpec {
    const pick = this.rng.pick(stream, this.beutel);
    return { ...pick, id: this.nextTokenId++, tags: [...pick.tags] };
  }

  escaped(token: TokenRuntime, from: Cabinet<never>, mode: EscapeMode, _at: Vec3): void {
    if (mode === 'block') {
      // a soft wall you own: shove it back where it came from rather than losing it
      if (token.body >= 0) {
        const p = this.phys.translation(token.body);
        const back = v3(from.origin.x - p.x, 0, from.origin.z - p.z);
        const d = Math.hypot(back.x, back.z) || 1;
        this.phys.setLinvel(token.body, v3((back.x / d) * 2.4, 1.6, (back.z / d) * 2.4));
        this.posVersion++;
      }
      return;
    }
    if (mode === 'room') {
      const targetId = NEIGHBOUR[from.id];
      const target = targetId ? this.cabinets.get(targetId) : null;
      if (target && targetId && token.body >= 0) {
        // the arcade is one physical room: the coin rolls into the next cabinet
        this.phys.setTranslation(token.body, target.intakePoint());
        this.phys.setLinvel(token.body, v3(0, -1, 0));
        this.posVersion++;
        token.machine = targetId;
        token.age = 0;
        this.tokensDirty = true;
        this.emit({ t: 'drop', token: token.id, machine: targetId });
        return;
      }
    }
    this.removeToken(token.id, 'lost');
  }

  /**
   * Which cabinets are live this tick: the one you are at, plus its neighbour when Der
   * Deckel has taken the lids off (so a token really can cross the aisle and land).
   */
  simulates(id: MachineId): boolean {
    const active = this.activeMachine;
    if (active === null) return false;
    if (id === active) return true;
    const deckel = this.pipe.query('escape', ctxFor(active, this.tick, null), 'lose') !== 'lose';
    return deckel && NEIGHBOUR[active] === id;
  }

  // ------------------------------------------------------------------ the loop

  step(): void {
    this.tick++;
    this.chargeLoudness();

    // 1. gravity is a question, not a constant (Das Pendel)
    const g = this.pipe.query('gravity', ctxFor(this.machine, this.tick, null), v3(0, -9.81, 0));
    this.phys.setGravity(g);

    // 2. per-token tick: this is where the tag physics defaults live
    const every = this.pipe.needsEveryTokenTick;
    const ids = this.order.slice();
    for (const id of ids) {
      const t = this.tokens.get(id);
      if (!t || !t.alive || !t.live) continue;
      t.age += DT;
      if (t.tags.length === 0 && !every) continue;
      this.pipe.fire('onTokenTick', { ...ctxFor(t.machine, this.tick, t), api: this as SimApi }, { dt: DT });
    }

    // 3. machine-level tick hooks
    for (const c of this.cabinets.values()) {
      this.pipe.fire('onMachineTick', { ...ctxFor(c.id, this.tick, null), api: this as SimApi }, { dt: DT });
    }

    // 4. physics
    const hits = this.phys.step();
    this.posVersion++;

    // 5. collisions
    for (const h of hits) {
      const ta = h.a >= 0 ? this.tokens.get(this.tokenByBody.get(h.a) ?? -1) ?? null : null;
      const tb = h.b >= 0 ? this.tokens.get(this.tokenByBody.get(h.b) ?? -1) ?? null : null;
      if (!ta && !tb) continue;
      const primary = ta ?? tb!;
      const other = ta && tb ? (primary === ta ? tb : ta) : null;
      const at = this.position(primary.id) ?? v3(0, 0, 0);
      if (h.impulse > 0.4) {
        this.emit({
          t: 'collision',
          a: primary.id,
          b: other ? other.id : null,
          impulse: h.impulse,
          at: [at.x, at.y, at.z],
        });
      }
      if (ta) {
        this.pipe.fire(
          'onCollision',
          { ...ctxFor(ta.machine, this.tick, ta), api: this as SimApi },
          { a: ta.id, b: tb ? tb.id : null, impulse: h.impulse, at, anchor: h.anchor },
        );
      }
      if (tb) {
        this.pipe.fire(
          'onCollision',
          { ...ctxFor(tb.machine, this.tick, tb), api: this as SimApi },
          { a: tb.id, b: ta ? ta.id : null, impulse: h.impulse, at, anchor: h.anchor },
        );
      }
    }

    // 6. machine logic: pusher motion, pocket landings, limits.
    //
    // architecture.md: "far cabinets fully frozen (only the active cabinet + Deckel-
    // neighbours simulate)". This is not only the perf rule it was written as — it is an
    // ECONOMY rule, and leaving it out is how an arcade prints money. With Der Deckel
    // owned, tokens cycle physically between adjacent cabinets; if every cabinet keeps
    // resolving pocket landings while you are three machines away, that loop pays out
    // forever with nobody standing at it. An unattended cabinet holds its state and waits.
    for (const c of this.cabinets.values()) {
      if (this.simulates(c.id)) c.tickMachine(DT);
      else c.tickShared(DT);
    }

    // 7. echoes
    this.runEchoes();

    // 8. idle (only for tokens anyone could care about)
    for (const id of ids) {
      const t = this.tokens.get(id);
      if (!t || !t.alive || t.body < 0 || t.tags.length === 0) continue;
      const v = this.phys.linvel(t.body);
      const speed = Math.abs(v.x) + Math.abs(v.y) + Math.abs(v.z);
      const before = Math.floor(t.idleSeconds);
      t.idleSeconds = speed < 0.08 ? t.idleSeconds + DT : 0;
      const after = Math.floor(t.idleSeconds);
      if (after > before) {
        this.pipe.fire(
          'onTokenIdle',
          { ...ctxFor(t.machine, this.tick, t), api: this as SimApi },
          { token: t.id, seconds: after },
        );
      }
    }
  }

  private runEchoes(): void {
    if (this.echoes.length === 0) return;
    const due = this.echoes.filter((e) => e.dueTick <= this.tick);
    if (due.length === 0) return;
    this.echoes = this.echoes.filter((e) => e.dueTick > this.tick);
    for (const e of due) {
      const cab = this.cabinets.get(e.machine);
      if (!cab) continue;
      this.emit({ t: 'echo', token: e.token, value: e.value, at: [e.at.x, e.at.y, e.at.z] });
      // an echo is a real trip through the payout pipeline, with a ghost of the token
      const ghost = makeRuntime(
        { id: e.token, material: e.material, faceValue: e.value, radius: 0.5, tags: e.tags },
        e.machine,
      );
      const res = runPayout(
        this.pipe,
        {
          token: ghost,
          machine: e.machine,
          tick: this.tick,
          base: e.value,
          jackpot: false,
          straehne: cab.straehne,
          topf: cab.topf,
          floorMult: floorMultiplier(this.economy.floor),
          ticketRatio: TICKET_RATIO[e.machine] ?? 0.25,
        },
        (ev) => this.emit(ev),
      );
      this.economy.addCoins(res.muenzen);
      this.economy.addTickets(res.tickets);
      this.emit({ t: 'payout', muenzen: res.muenzen, tickets: res.tickets, machine: e.machine });
    }
  }

  // ------------------------------------------------------------------ inputs

  applyInput(i: SimInput): void {
    if (i.verb === 'meta') {
      // walk / enter / leave / inspect / brecheisen / buy / boss / descend — these change
      // the run, not the cabinet, so they belong in the log exactly like a coin drop does.
      if (this.onMeta) this.onMeta(i.payload as MetaVerb);
      return;
    }
    // …and neither is a machine verb. The night is over; the levers do nothing.
    if (this.over) return;
    const c = this.cabinets.get(i.machine);
    if (!c) return;
    this.activeMachine = i.machine;
    switch (i.verb) {
      case 'setzen':
        this.doSetzen(i.machine, i.payload as SetzenPayload);
        break;
      case 'ruetteln':
        c.ruetteln(typeof i.payload === 'number' ? i.payload : 1);
        break;
      case 'glueck': {
        const p = (i.payload ?? {}) as { stream?: string; event?: string };
        c.glueckSpend(p.stream ?? 'reels', p.event ?? 'zug');
        break;
      }
      case 'kassieren':
        c.kassieren();
        break;
      case 'nachlegen':
        c.nachlegen();
        break;
    }
  }

  private doSetzen(machine: MachineId, p: SetzenPayload | undefined): void {
    if (!p) return;
    if (machine === 'pachinko' && p.machine === 'pachinko') {
      const pk = this.pachinko();
      const spec = this.stake(p.beutel);
      if (pk && spec) pk.setzen({ angle: p.angle, power: p.power, spec });
    } else if (machine === 'muenzschieber' && p.machine === 'muenzschieber') {
      const ms = this.pile();
      const spec = this.stake(p.beutel);
      if (ms && spec) ms.setzen({ x: p.x, z: p.z, spec });
    } else if (machine === 'bandit' && p.machine === 'bandit') {
      const b = this.bandit();
      if (b && this.beutel.length > 0) b.setzen({ power: p.power, stake: p.stake } satisfies BanditSetzen);
    } else if (machine === 'roulette' && p.machine === 'roulette') {
      const r = this.roulette();
      const spec = this.stake(p.beutel);
      if (r && spec) r.setzen({ pocket: p.pocket, power: p.power, spec });
    } else if (machine === 'flipper' && p.machine === 'flipper') {
      const f = this.flipper();
      if (!f) return;
      // Only the plunger consumes a token from the bag; the bats are hands, not stakes.
      if (p.flip === 'plunger') {
        const spec = this.stake(p.beutel);
        if (spec) f.setzen({ flip: 'plunger', power: p.power, spec });
      } else {
        f.setzen({ flip: p.flip, power: p.power, spec: this.beutel[0] ?? defaultBeutel()[0]! });
      }
    } else if (machine === 'greifer' && p.machine === 'greifer') {
      const g = this.greifer();
      const spec = this.stake(p.beutel);
      if (g && spec) g.setzen({ x: p.x, z: p.z, spec });
    }
  }

  /**
   * Staking a token takes it out of Der Beutel conceptually; the spec is copied.
   *
   * An EMPTY bag is a real, reachable state — Der Croupier takes tokens in kind at a
   * failed Abrechnung and Der Rausschmeißer takes your best one — so it must be a
   * survivable answer rather than a crash. It returns null; `doSetzen` then does nothing,
   * and the run director reads an empty bag as "nothing left worth taking".
   */
  private stake(index: number): TokenSpec | null {
    if (this.beutel.length === 0) return null;
    const i = ((index % this.beutel.length) + this.beutel.length) % this.beutel.length;
    const s = this.beutel[i]!;
    this.pipe.fire(
      'onStake',
      { ...ctxFor(this.machine, this.tick, null), api: this as SimApi },
      { tokens: [s.id] },
    );
    return { ...s, tags: [...s.tags] };
  }

  /** Advance to `untilTick`, applying the input log on the way. Deterministic. */
  run(untilTick: number, inputs: readonly SimInput[] = []): void {
    const queue = [...inputs].sort((a, b) => a.tick - b.tick);
    let qi = 0;
    while (this.tick < untilTick) {
      while (qi < queue.length && queue[qi]!.tick <= this.tick) this.applyInput(queue[qi++]!);
      this.step();
    }
    while (qi < queue.length && queue[qi]!.tick <= this.tick) this.applyInput(queue[qi++]!);
  }

  // ------------------------------------------------------------------ output

  runState(): RunState {
    return {
      tick: this.tick,
      floor: this.economy.floor,
      wunder: this.wunder,
      wunderCard: this.wunderCard,
      fotoHeld: this.fotoHeld,
      phase: this.phase,
      muenzen: this.economy.muenzen,
      tickets: this.economy.tickets,
      glueck: this.economy.glueck,
      verdacht: this.economy.verdacht,
      debtDue: this.economy.debtDue,
      ticketQuota: this.ticketQuota,
      beutel: this.beutel.map((s) => ({ ...s, tags: [...s.tags] })),
      items: itemRefs(this.itemIds, this.pipe),
      activeMachine: this.activeMachine,
      hausverbot: this.economy.hausverbot,
      over: this.over,
      won: this.won,
    };
  }

  /**
   * **VERDACHT for winning loudly.** house-and-heat.md's first and most important source
   * — "winning streaks above expectation (the pipeline knows the odds, so 'above
   * expectation' is computable and honest)" — and until now the only thing that was
   * actually implemented was `jackpot +3`, plus shoves and bank breaks.
   *
   * That gap was invisible while a coin-pusher avalanche was miscounting itself as a
   * dozen jackpots and pushing VERDACHT to 58 in twenty seconds. Fixing the avalanche
   * revealed it: a whole autoplayed run then peaked at VERDACHT **3**, so the House never
   * reached 30, never installed a single rigging, and never sent the bouncer. Half the
   * antagonist was switched off.
   *
   * So: a five-second window of winnings, compared against what this floor is *expected*
   * to pay in that window, converted to heat once a second. Rate-limited on purpose —
   * charging per scoring event is exactly the mistake the avalanche made, and a
   * twenty-coin cascade must be one loud moment, not twenty.
   */
  private chargeLoudness(): void {
    if (this.tick % TICK_HZ !== 0) return;
    const cutoff = this.tick - TICK_HZ * 5;
    while (this.winWindow.length > 0 && this.winWindow[0]!.tick < cutoff) this.winWindow.shift();
    let won = 0;
    for (const w of this.winWindow) won += w.amount;
    // what an ordinary five seconds of honest play pays on this floor
    const expected = 60 * floorMultiplier(this.economy.floor);
    if (won <= expected * 3) return;
    const ratio = won / expected;
    const heat = ratio > 12 ? 5 : ratio > 7 ? 3 : 1;
    this.economy.addVerdacht(heat, 'auffällig hohe Gewinne', (e) => this.emit(e));
  }

  /** contract v1: the aisle is the character sheet. Cheap — no token arrays. */
  roomState(): AisleCabinet[] {
    const out: AisleCabinet[] = [];
    for (const id of [...this.cabinets.keys()].sort()) {
      const c = this.cabinets.get(id)!;
      // The Saalchef's table is not in the aisle. It is behind the door, and the door
      // only exists while you are through it — otherwise the room would advertise a
      // sixth cabinet you can neither walk to nor play.
      if (id === 'flipper' && this.phase !== 'boss') continue;
      out.push({
        machine: id,
        origin: [c.origin.x, c.origin.y, c.origin.z],
        topf: c.topf,
        topf01: Math.max(0, Math.min(1, c.topf / c.topfMax)),
        broken: c.broken,
        powered: c.powered,
        tokenCount: this.tokensIn(id).length,
        suspect: this.pipe.hasUndiscovered(id),
      });
    }
    return out;
  }

  snapshot(): Snapshot {
    const cab = this.activeMachine ? this.cabinets.get(this.activeMachine) ?? null : null;
    const s: Snapshot = {
      tick: this.tick,
      run: this.runState(),
      cabinet: cab ? cab.state() : null,
      room: this.roomState(),
      spieler: [this.spielerX, 0, this.spielerZ],
      events: this.events,
    };
    this.events = [];
    return s;
  }

  /** Peek at the event feed without draining it (tests, demo). */
  peekEvents(): readonly SimEvent[] {
    return this.events;
  }

  // ------------------------------------------------------------------ run surface
  // Everything the RunDirector (core/run.ts) needs to run a floor loop, kept here so the
  // director never reaches into private state and every mutation stays inside the hash.

  /** Meta verbs (walk / enter / buy / descend …) are routed to the director, if any. */
  onMeta: ((m: MetaVerb) => void) | null = null;

  beutelSpecs(): readonly TokenSpec[] {
    return this.beutel;
  }

  /**
   * **Der Beutel is never emptied completely.** Der Croupier is polite: he takes tokens in
   * kind, but he always leaves you carfare — one token to keep playing with.
   *
   * This is the slope-not-cliff rule made structural rather than tuned. A truly empty bag
   * is not a dramatic loss, it is an undefined game: you cannot stake, the BANDIT has no
   * reel strip (your deck IS the reel strip), roulette has no ball. Leaving one bare
   * copper keeps every machine well-defined while being, in every sense that matters,
   * nothing left worth taking — which is what `nothingLeftToTake()` reads.
   */
  removeFromBeutel(id: number): boolean {
    if (this.beutel.length <= 1) return false;
    const i = this.beutel.findIndex((s) => s.id === id);
    if (i < 0) return false;
    this.beutel.splice(i, 1);
    return true;
  }

  /** True when the bag holds nothing but bare, unenchanted small change. */
  bagIsBare(): boolean {
    return this.beutel.every((s) => s.tags.length === 0 && s.faceValue <= 1);
  }

  /** Der Croupier's favourite: the single most valuable thing you are carrying. */
  takeBestToken(): string | null {
    if (this.beutel.length <= 1) return null;
    let best = -1;
    let bestI = -1;
    this.beutel.forEach((s, i) => {
      const worth = s.faceValue * (1 + s.tags.length);
      if (worth > best) { best = worth; bestI = i; }
    });
    if (bestI < 0) return null;
    const [gone] = this.beutel.splice(bestI, 1);
    return gone ? `Marke #${gone.id}` : null;
  }

  /** An enchantment is a tag applied to a token; put it where it has room to matter. */
  enchantWeakest(tag: Tag): boolean {
    let bestI = -1;
    let bestScore = Infinity;
    this.beutel.forEach((s, i) => {
      if (s.tags.length >= 3 || s.tags.includes(tag)) return;
      // fewest tags first, then lowest face value — ties broken by index, so it is total
      const score = s.tags.length * 100 + s.faceValue;
      if (score < bestScore) { bestScore = score; bestI = i; }
    });
    if (bestI < 0) return false;
    this.beutel[bestI]!.tags.push(tag);
    return true;
  }

  addItem(id: string): void {
    if (!this.itemIds.includes(id)) this.itemIds.push(id);
  }

  removeItem(id: string): void {
    const i = this.itemIds.indexOf(id);
    if (i >= 0) this.itemIds.splice(i, 1);
  }

  /** The House withdraws from your bank at a failed Abrechnung (economy.md). */
  drainPile(fraction: number): number {
    const tokens = this.tokensIn('muenzschieber');
    const take = Math.floor(tokens.length * Math.max(0, Math.min(1, fraction)));
    let worth = 0;
    for (let i = 0; i < take; i++) {
      const t = tokens[i]!;
      worth += t.faceValue;
      this.removeToken(t.id, 'lost');
    }
    return worth;
  }

  /**
   * CABINET SNAPSHOTS (MASCHINENBAU cut 4). Body transforms plus token specs, which is
   * everything a pile *is*. architecture.md wants one mechanism serving both the floor
   * transition and the FOTOAUTOMAT, and this is it — the floor loop round-trips through
   * it on every descent, so the save path is exercised every run instead of only when
   * somebody saves.
   */
  snapshotCabinet(id: MachineId): CabinetSnapshot | null {
    const c = this.cabinets.get(id);
    if (!c) return null;
    const tokens: TokenSnapshot[] = [];
    for (const t of this.tokensIn(id)) {
      const p = t.body >= 0 ? this.phys.translation(t.body) : v3(0, 0, 0);
      const q = t.body >= 0 ? this.phys.rotation(t.body) : ([0, 0, 0, 1] as [number, number, number, number]);
      const v = t.body >= 0 ? this.phys.linvel(t.body) : v3(0, 0, 0);
      tokens.push({
        spec: { id: t.id, material: t.material, faceValue: t.faceValue, radius: t.radius, tags: [...t.tags] },
        pos: [p.x, p.y, p.z],
        quat: q,
        vel: [v.x, v.y, v.z],
        age: t.age,
        zinsClock: t.zinsClock,
        potency: t.potency,
        stuck: t.stuck,
        generation: t.generation,
      });
    }
    return {
      machine: id,
      topf: c.topf,
      straehne: c.straehne,
      limit01: c.limit01,
      broken: c.broken,
      stakeLevel: c.stakeLevel,
      entries: c.entries,
      tokens,
    };
  }

  restoreCabinet(id: MachineId, snap: CabinetSnapshot): boolean {
    const c = this.cabinets.get(id);
    if (!c || snap.machine !== id) return false;
    for (const t of [...this.tokensIn(id)]) this.removeToken(t.id, 'transfer');
    c.topf = snap.topf;
    c.straehne = snap.straehne;
    c.limit01 = snap.limit01;
    c.broken = snap.broken;
    c.stakeLevel = snap.stakeLevel;
    c.entries = snap.entries;
    for (const t of snap.tokens) {
      const tok = this.spawnToken(
        t.spec,
        id,
        v3(t.pos[0], t.pos[1], t.pos[2]),
        v3(t.vel[0], t.vel[1], t.vel[2]),
      );
      tok.age = t.age;
      tok.zinsClock = t.zinsClock;
      tok.potency = t.potency;
      tok.generation = t.generation;
      // a [Kleber] stalagmite is terrain: it must come back as terrain, in place
      if (t.stuck) this.freeze(tok.id);
    }
    this.tokensDirty = true;
    return true;
  }

  drainEvents(): SimEvent[] {
    const e = this.events;
    this.events = [];
    return e;
  }

  /**
   * The twin-run hash: everything that could possibly diverge, in a fixed order,
   * hashed by exact float bits.
   */
  hashWorld(): string {
    const h = new Hasher();
    h.u32(this.tick);
    h.i32(this.economy.muenzen).i32(this.economy.tickets);
    h.i32(this.economy.glueck).i32(this.economy.verdacht).i32(this.economy.floor);
    h.i32(this.economy.pityDebt());

    // resolved payout stage order — an order bug is a determinism bug
    h.str(orderKey(this.pipe.query('stageOrder', ctxFor(this.machine, this.tick, null), [
      'base', 'material', 'straehne', 'topf', 'floor',
    ])));

    const machineIds = [...this.cabinets.keys()].sort();
    for (const id of machineIds) {
      const c = this.cabinets.get(id)!;
      h.str(id).f64(c.topf).f64(c.straehne).f64(c.limit01).f64(c.tilt01);
      h.bool(c.broken).i32(c.stakeLevel);
    }

    h.u32(this.order.length);
    for (const tid of this.order) {
      const t = this.tokens.get(tid);
      if (!t) continue;
      h.u32(tid).f64(t.faceValue).f64(t.radius).f64(t.age).f64(t.zinsClock).f64(t.potency);
      h.u32(MATERIAL_INDEX[t.material] ?? 0);
      h.u32(t.tags.length);
      for (const tag of t.tags) h.u32(TAG_INDEX[tag]);
      h.bool(t.stuck).u32(t.ghost === 'armed' ? 1 : t.ghost === 'phasing' ? 2 : 3);
      if (t.body >= 0) {
        const p = this.phys.translation(t.body);
        const q = this.phys.rotation(t.body);
        const v = this.phys.linvel(t.body);
        h.f64(p.x).f64(p.y).f64(p.z);
        h.f64(q[0]).f64(q[1]).f64(q[2]).f64(q[3]);
        h.f64(v.x).f64(v.y).f64(v.z);
        h.bool(this.phys.isSleeping(t.body));
      } else {
        h.u32(0xdeadbeef);
      }
    }

    for (const s of this.rng.snapshot()) {
      h.str(s.label).u32(s.a).u32(s.b).u32(s.c).u32(s.d).u32(s.draws);
    }
    return h.hex();
  }

  /** Bodies Rapier is actually integrating — the perf story in one number. Sedimented
   *  and [Kleber]-stuck coins are fixed bodies and cost nothing, so they don't count. */
  awakeBodies(): number {
    const handles: number[] = [];
    for (const id of this.order) {
      const t = this.tokens.get(id);
      if (t && t.alive && t.body >= 0 && !t.sedimented && !t.stuck) handles.push(t.body);
    }
    return this.phys.awakeCount(handles);
  }

  /** coins currently packed as sediment (fixed bodies) across all cabinets */
  sedimentedCount(): number {
    let n = 0;
    for (const id of this.order) {
      const t = this.tokens.get(id);
      if (t && t.alive && t.sedimented) n++;
    }
    return n;
  }

  liveTokenCount(): number {
    return this.order.length;
  }

  materialMultOf(t: TokenRuntime): number {
    return MATERIAL_MULT[t.material];
  }

  free(): void {
    this.phys.free();
  }
}

const EMPTY_TOKENS: TokenRuntime[] = [];

/** A starting bag: enough copper to play, a little silver, one gold. */
export function defaultBeutel(): TokenSpec[] {
  const out: TokenSpec[] = [];
  let id = 1;
  for (let i = 0; i < 6; i++) out.push({ id: id++, material: 'kupfer', faceValue: 1, radius: 0.5, tags: [] });
  for (let i = 0; i < 3; i++) out.push({ id: id++, material: 'silber', faceValue: 2, radius: 0.5, tags: [] });
  out.push({ id: id++, material: 'gold', faceValue: 4, radius: 0.5, tags: [] });
  return out;
}

export { TICK_HZ, DT };
